'use client'

import React, { useMemo, useState } from 'react'
import type { CampAnmeldung, Teilnehmer, Camp } from '@/payload-types'
import {
  ChevronDownIcon,
  ChevronUpIcon,
  Edit,
  Check,
  CheckSquare,
  Square,
  Mail,
  X,
  ArrowRight,
} from 'lucide-react'
import { StatusManagementDialog } from './StatusManagementDialog'
import { BulkStatusManagementDialog } from './BulkStatusManagementDialog'
import { useRouter } from 'next/navigation'
import { sendFreeCampConfirmationEmail } from '@/actions/freeCampAnmeldung'
import { describeDuplicateHints, type DuplicateHint } from '@/utilities/duplicateRegistrations'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card'
import { isNothingToPay } from '@/utilities/paymentDisplay'
import { campAsksMultiPitchInterest } from '@/utilities/registrationCampFieldUtils'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

type PopulatedAnmeldung = CampAnmeldung & {
  teilnehmer: Teilnehmer
  camp: Camp
  alternativeCamp?: Camp | null
  pastCamps?: Array<{
    id: string
    title: string
    date: string
    location: string
    status: string
    registeredAt: string
    isFuture?: boolean // Flag to indicate if this is a future camp
  }>
  // Submitted, undecided registrations for other camps that are still to come, soonest first
  openFutureRegistrations?: Array<{
    campId: string
    title: string
    date: string
    email?: string
    otherEmail?: boolean // Registered with another email but the same name and birthday
  }>
  isFreeRegistration?: boolean
  name?: string // For free registrations
  email?: string // For free registrations
  duplicateHints?: DuplicateHint[] // Other registrations of this camp with the same name
}

type SortField =
  | 'teilnehmer'
  | 'status'
  | 'campCount'
  | 'lastCampDate'
  | 'paymentStatus'
  | 'needsEquipment'
  | 'needsLiftTicket'
  | 'interestMultiPitch'
  | 'createdAt'
type SortDirection = 'asc' | 'desc'

interface AnmeldungenTableProps {
  anmeldungen: PopulatedAnmeldung[]
  selectedAnmeldungen: Set<string>
  onSelectAnmeldung: (anmeldungId: string) => void
  onSelectAll: () => void
  onBulkStatusUpdate: () => void
  currentCampId?: string
  isAlternativeView?: boolean
  onSwitchToMainTab?: () => void
}

// Status Badge Component
const StatusBadge = ({ status }: { status: string | null | undefined }) => {
  const getStatusClasses = (status: string | null | undefined) => {
    switch (status) {
      case 'submitted':
        return 'bg-amber-100 text-amber-800 border-amber-300'
      case 'confirmed':
        return 'bg-green-100 text-green-800 border-green-300'
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-300'
      case 'waitlist':
        return 'bg-gray-100 text-gray-700 border-gray-300'
      default:
        return 'bg-gray-100 text-gray-500 border-gray-200'
    }
  }

  const getStatusText = (status: string | null | undefined) => {
    switch (status) {
      case 'submitted':
        return 'Eingereicht'
      case 'confirmed':
        return 'Bestätigt'
      case 'rejected':
        return 'Abgelehnt'
      case 'waitlist':
        return 'Warteliste'
      default:
        return 'Unbekannt'
    }
  }

  return (
    <span
      className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide border ${getStatusClasses(status)}`}
    >
      {getStatusText(status)}
    </span>
  )
}

// Participant Avatar Component
const ParticipantAvatar = ({
  teilnehmer,
  isFreeRegistration,
  name,
}: {
  teilnehmer: Teilnehmer
  isFreeRegistration?: boolean
  name?: string
}) => {
  const initial = isFreeRegistration ? name?.[0] || 'F' : teilnehmer?.firstName?.[0] || 'T'
  const bgColor = isFreeRegistration ? 'from-purple-500 to-purple-700' : 'from-blue-500 to-blue-700'

  return (
    <div
      className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br ${bgColor}`}
    >
      <span className="text-sm font-semibold text-white">{initial}</span>
    </div>
  )
}

// Participant Info Component
const ParticipantInfo = ({
  teilnehmer,
  isFreeRegistration,
  name,
  duplicateHints,
}: {
  teilnehmer: Teilnehmer
  isFreeRegistration?: boolean
  name?: string
  duplicateHints?: DuplicateHint[]
}) => (
  <div>
    <div className="flex items-center gap-2 font-semibold text-gray-900">
      {isFreeRegistration ? (
        <>
          {name}
          <span className="inline-flex items-center rounded-full border border-purple-300 bg-purple-100 px-2 py-1 text-xs font-semibold text-purple-800">
            Intern
          </span>
        </>
      ) : (
        <>
          {teilnehmer?.firstName} {teilnehmer?.lastName}
          {teilnehmer?.isBlacklisted && (
            <span className="inline-flex items-center rounded-full border border-red-300 bg-red-100 px-2 py-1 text-xs font-semibold text-red-800">
              Blacklist
            </span>
          )}
          {duplicateHints && duplicateHints.length > 0 && (
            <span
              title={describeDuplicateHints(duplicateHints)}
              className="inline-flex cursor-help items-center rounded-full border border-orange-300 bg-orange-100 px-2 py-1 text-xs font-semibold text-orange-800"
            >
              Dublette?
            </span>
          )}
          <span className="ml-4 rounded-full bg-gray-100 px-2 text-center text-xs text-gray-600">
            {teilnehmer.country}
          </span>
        </>
      )}
    </div>
    {teilnehmer?.email && <div className="mt-0.5 text-xs text-gray-600">{teilnehmer.email}</div>}{' '}
    {teilnehmer?.birthday && (
      <div className="mt-0.5 text-xs text-gray-600">
        {new Date(teilnehmer.birthday).getFullYear()}
      </div>
    )}
  </div>
)

// Payment Status Badge Component
const PaymentStatusBadge = ({ anmeldung }: { anmeldung: PopulatedAnmeldung }) => {
  const [isVerifying, setIsVerifying] = useState(false)
  const router = useRouter()

  // Free registrations have no payment status
  if (anmeldung.isFreeRegistration) {
    return <span className="text-xs text-gray-400">-</span>
  }

  // Check if there's anything to pay for
  const hasPaymentRequired =
    anmeldung.isPaidCamp ||
    anmeldung.needsLiftTicket ||
    anmeldung.children?.some((child) => child.needsLiftTicket)

  if (!hasPaymentRequired) {
    return (
      <span className="inline-flex items-center rounded-full border border-green-300 bg-green-100 px-2 py-1 text-xs font-semibold uppercase tracking-wide text-green-800">
        Kostenfrei
      </span>
    )
  }

  if (isNothingToPay(anmeldung)) {
    return (
      <span
        title="Es war nichts zu bezahlen, die Anmeldung wurde automatisch auf bezahlt gesetzt."
        className="inline-flex items-center rounded-full border border-gray-300 bg-gray-100 px-2 py-1 text-xs font-semibold uppercase tracking-wide text-gray-700"
      >
        Nichts zu zahlen
      </span>
    )
  }

  const getPaymentStatusClasses = (status: string | null | undefined) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800 border-green-300'
      case 'pending':
        return 'bg-amber-100 text-amber-800 border-amber-300'
      case 'cancelled':
        return 'bg-gray-100 text-gray-700 border-gray-300'
      case 'refunded':
        return 'bg-blue-100 text-blue-800 border-blue-300'
      default:
        return 'bg-amber-100 text-amber-800 border-amber-300'
    }
  }

  const getPaymentStatusText = (status: string | null | undefined) => {
    switch (status) {
      case 'paid':
        return 'Bezahlt'
      case 'pending':
        return 'Ausstehend'
      case 'cancelled':
        return 'Storniert'
      case 'refunded':
        return 'Erstattet'
      default:
        return 'Ausstehend'
    }
  }

  const handleVerifyPayment = async () => {
    // Only verify if there's a Mollie payment ID and status is not already paid
    if (!anmeldung.molliePaymentId || anmeldung.paymentStatus === 'paid' || isVerifying) {
      return
    }

    setIsVerifying(true)

    try {
      const response = await fetch('/api/payments/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ registrationId: anmeldung.id }),
      })

      const data = await response.json()

      // If payment status changed, refresh the page
      if (data.success && data.paymentStatus !== anmeldung.paymentStatus) {
        router.refresh()
      }
    } catch (error) {
      console.error('Payment verification error:', error)
    } finally {
      setIsVerifying(false)
    }
  }

  const canVerify = anmeldung.molliePaymentId && anmeldung.paymentStatus !== 'paid'

  return (
    <span
      onClick={handleVerifyPayment}
      className={`inline-flex items-center justify-center gap-1.5 px-2 py-1 rounded-full text-xs font-semibold uppercase tracking-wide border ${getPaymentStatusClasses(anmeldung.paymentStatus)} ${
        canVerify ? 'cursor-pointer hover:opacity-80 transition-opacity ' : ''
      } ${isVerifying ? 'opacity-70' : ''}`}
      title={
        canVerify
          ? 'Klicken um Zahlungsstatus zu überprüfen (Mollie Payment ID vorhanden)'
          : anmeldung.paymentStatus !== 'paid' && !anmeldung.molliePaymentId
            ? 'Noch keine Zahlung erstellt'
            : undefined
      }
    >
      {isVerifying ? (
        <span className="inline-block h-2.5 w-2.5 animate-spin rounded-full border border-current border-t-transparent" />
      ) : (
        <>
          {getPaymentStatusText(anmeldung.paymentStatus)}
          {canVerify && <span className="ml-0.5 text-[10px]">↻</span>}
        </>
      )}
    </span>
  )
}

// Sortable Table Header Component
interface SortableHeaderProps {
  field: SortField
  currentSort: SortField
  currentDirection: SortDirection
  onSort: (field: SortField) => void
  children: React.ReactNode
  textAlign?: 'left' | 'center'
}

const SortableHeader = ({
  field,
  currentSort,
  currentDirection,
  onSort,
  children,
  textAlign = 'left',
}: SortableHeaderProps) => (
  <th
    className="cursor-pointer select-none border-r border-gray-200 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-700 hover:bg-gray-50"
    onClick={() => onSort(field)}
  >
    <div
      className={`flex items-center gap-2 ${textAlign === 'center' ? 'justify-center' : 'justify-start'}`}
    >
      {children}
      {currentSort === field &&
        (currentDirection === 'asc' ? (
          <ChevronUpIcon className="h-3 w-3" />
        ) : (
          <ChevronDownIcon className="h-3 w-3" />
        ))}
    </div>
  </th>
)

// Enhanced Checkbox Component
interface EnhancedCheckboxProps {
  isSelected: boolean
  onClick: () => void
  size?: 'sm' | 'md'
}

const EnhancedCheckbox: React.FC<EnhancedCheckboxProps> = ({
  isSelected,
  onClick,
  size = 'md',
}) => {
  const sizeClasses = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5'
  const iconSize = size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'

  return (
    <button
      onClick={onClick}
      className={`
        ${sizeClasses} 
        rounded border-2 flex items-center justify-center transition-all duration-200
        ${
          isSelected
            ? 'bg-blue-600 border-blue-600 text-white shadow-md'
            : 'bg-white border-gray-300 hover:border-blue-400 hover:bg-custom-blue'
        }
        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1
      `}
    >
      {isSelected && <Check className={`${iconSize} text-white`} />}
    </button>
  )
}

// Main Table Component
const AnmeldungenTable: React.FC<AnmeldungenTableProps> = ({
  anmeldungen,
  selectedAnmeldungen,
  onSelectAnmeldung,
  onSelectAll,
  onBulkStatusUpdate,
  currentCampId,
  isAlternativeView = false,
  onSwitchToMainTab,
}) => {
  const [sortField, setSortField] = useState<SortField>('teilnehmer')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const [selectedAnmeldung, setSelectedAnmeldung] = useState<PopulatedAnmeldung | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isBulkDialogOpen, setIsBulkDialogOpen] = useState(false)
  const [isEmailConfirmOpen, setIsEmailConfirmOpen] = useState(false)
  const [emailAnmeldung, setEmailAnmeldung] = useState<PopulatedAnmeldung | null>(null)
  const [isMoveConfirmOpen, setIsMoveConfirmOpen] = useState(false)
  const [anmeldungToMove, setAnmeldungToMove] = useState<PopulatedAnmeldung | null>(null)
  const [isMoving, setIsMoving] = useState(false)
  const router = useRouter()

  // Helper to format verification date as (11.1.26)
  const formatVerifiedDate = (isoDate?: string | null) => {
    if (!isoDate) return null
    const date = new Date(isoDate)
    if (Number.isNaN(date.getTime())) return null
    const day = date.getDate()
    const month = date.getMonth() + 1
    const year = date.getFullYear() % 100
    return `${day}.${month}.${year.toString().padStart(2, '0')}`
  }

  // Only camps that ask for multi-pitch interest have something to show in this column; an
  // answer already given stays visible, e.g. after a registration moved to another camp.
  const showMultiPitchColumn = useMemo(
    () =>
      anmeldungen.some(
        (anmeldung) =>
          anmeldung.interestMultiPitch ||
          (typeof anmeldung.camp === 'object' && campAsksMultiPitchInterest(anmeldung.camp)),
      ),
    [anmeldungen],
  )

  // Group anmeldungen by participant to calculate camp count and last camp date
  const participantData = useMemo(() => {
    const participantMap = new Map<
      string,
      {
        teilnehmer: Teilnehmer
        anmeldungen: PopulatedAnmeldung[]
        campCount: number
        lastCampDate: Date | null
        latestStatus: string | null
        pastCamps: Array<{
          id: string
          title: string
          date: string
          location: string
          status: string
          registeredAt: string
          isFuture?: boolean
        }>
      }
    >()

    anmeldungen.forEach((anmeldung) => {
      const teilnehmerId = (anmeldung.teilnehmer as Teilnehmer)?.id
      if (!teilnehmerId) return

      // Initialize participant data
      if (!participantMap.has(teilnehmerId)) {
        participantMap.set(teilnehmerId, {
          teilnehmer: anmeldung.teilnehmer as Teilnehmer,
          anmeldungen: [],
          campCount: 0,
          lastCampDate: null,
          latestStatus: null,
          pastCamps: [],
        })
      }

      const participant = participantMap.get(teilnehmerId)!

      // Add the anmeldung to the participant's anmeldungen array
      participant.anmeldungen.push(anmeldung)

      // Use pastCamps data for consistent counting
      // Add both current system's past camps and historical count from old system
      participant.campCount =
        (anmeldung.pastCamps?.length || 0) +
        (participant?.teilnehmer?.historicalRegistrationCount || 0)

      // Update pastCamps - limit to max 1 past camp and 1 future camp for display
      if (anmeldung.pastCamps && anmeldung.pastCamps.length > 0) {
        // Separate past and future camps
        const pastCamps = anmeldung.pastCamps.filter((camp) => !camp.isFuture)
        const futureCamps = anmeldung.pastCamps.filter((camp) => camp.isFuture)

        // Limit to max 1 past camp (most recent) and 1 future camp (next upcoming)
        const displayCamps: typeof anmeldung.pastCamps = []
        if (pastCamps.length > 0 && pastCamps[0]) {
          displayCamps.push(pastCamps[0]) // Most recent past camp
        }
        if (futureCamps.length > 0 && futureCamps[0]) {
          displayCamps.push(futureCamps[0]) // Next future camp
        }

        participant.pastCamps = displayCamps

        // Set lastCampDate for sorting (use most recent past camp if available, otherwise future camp)
        if (pastCamps.length > 0) {
          const mostRecentPastCamp = pastCamps[0]
          if (mostRecentPastCamp) {
            participant.lastCampDate = new Date(mostRecentPastCamp.date)
          }
        } else if (futureCamps.length > 0) {
          const mostRecentFutureCamp = futureCamps[0]
          if (mostRecentFutureCamp) {
            participant.lastCampDate = new Date(mostRecentFutureCamp.date)
          }
        } else {
          participant.lastCampDate = null
        }
      } else {
        participant.pastCamps = []
        participant.lastCampDate = null
      }

      // Track latest status (from all registrations)
      if (
        !participant.latestStatus ||
        anmeldung.createdAt > (participant.anmeldungen[0]?.createdAt || '')
      ) {
        participant.latestStatus = anmeldung.status || null
      }
    })

    return Array.from(participantMap.values())
  }, [anmeldungen])

  // Sort participants based on current sort field and direction
  const sortedParticipants = useMemo(() => {
    return [...participantData].sort((a, b) => {
      let aValue: any
      let bValue: any

      switch (sortField) {
        case 'teilnehmer':
          aValue = `${a.teilnehmer?.lastName || ''} ${a.teilnehmer?.firstName || ''}`.toLowerCase()
          bValue = `${b.teilnehmer?.lastName || ''} ${b.teilnehmer?.firstName || ''}`.toLowerCase()
          break
        case 'status':
          const statusPriority: Record<string, number> = {
            submitted: 1,
            waitlist: 2,
            confirmed: 3,
            rejected: 4,
          }
          aValue = statusPriority[a.latestStatus || 'submitted'] || 6
          bValue = statusPriority[b.latestStatus || 'submitted'] || 6
          break
        case 'campCount':
          aValue = a.campCount
          bValue = b.campCount
          break
        case 'lastCampDate':
          aValue = a.lastCampDate?.getTime() || 0
          bValue = b.lastCampDate?.getTime() || 0
          break
        case 'paymentStatus':
          const paymentPriority: Record<string, number> = {
            pending: 1,
            paid: 2,
            cancelled: 3,
            refunded: 4,
          }
          aValue = paymentPriority[a.anmeldungen[0]?.paymentStatus || 'pending'] || 5
          bValue = paymentPriority[b.anmeldungen[0]?.paymentStatus || 'pending'] || 5
          break
        case 'needsEquipment':
          aValue = a.anmeldungen[0]?.needsEquipment ? 1 : 0
          bValue = b.anmeldungen[0]?.needsEquipment ? 1 : 0
          break
        case 'needsLiftTicket':
          aValue = a.anmeldungen[0]?.needsLiftTicket ? 1 : 0
          bValue = b.anmeldungen[0]?.needsLiftTicket ? 1 : 0
          break
        case 'interestMultiPitch':
          aValue = a.anmeldungen[0]?.interestMultiPitch ? 1 : 0
          bValue = b.anmeldungen[0]?.interestMultiPitch ? 1 : 0
          break
        case 'createdAt':
          aValue = new Date(a.anmeldungen[0]?.createdAt || '').getTime() || 0
          bValue = new Date(b.anmeldungen[0]?.createdAt || '').getTime() || 0
          break
        default:
          aValue = a.teilnehmer?.lastName || ''
          bValue = b.teilnehmer?.lastName || ''
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
      return 0
    })
  }, [participantData, sortField, sortDirection])

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const handleStatusUpdate = async (
    anmeldungId: string,
    newStatus: string,
    notes?: string,
    alternativeCampId?: string,
    children?: Array<{
      name: string
      birthday: string | undefined
      needsLiftTicket: boolean
      needsEquipment: boolean
    }>,
  ) => {
    try {
      const response = await fetch(`/api/admin/camp-anmeldung/${anmeldungId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus, notes, alternativeCampId, children }),
      })

      const result = await response.json()

      if (result.success) {
        // Use Payload's toast system
        window.dispatchEvent(
          new CustomEvent('payload-toast', {
            detail: {
              type: 'success',
              message: 'Status erfolgreich aktualisiert!',
            },
          }),
        )

        // Refresh the server component data
        router.refresh()
      } else {
        window.dispatchEvent(
          new CustomEvent('payload-toast', {
            detail: {
              type: 'error',
              message: result.error || 'Fehler beim Aktualisieren des Status',
            },
          }),
        )
      }
    } catch (error) {
      console.error('Error updating status:', error)
      window.dispatchEvent(
        new CustomEvent('payload-toast', {
          detail: {
            type: 'error',
            message: 'Fehler beim Aktualisieren des Status',
          },
        }),
      )
    }
  }

  const handleCampChange = async (anmeldungId: string, newCampId: string, notes?: string) => {
    try {
      const response = await fetch('/api/admin/camp-anmeldung/change-camp', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ anmeldungId, newCampId, notes }),
      })

      const result = await response.json()

      if (result.success) {
        // Use Payload's toast system
        window.dispatchEvent(
          new CustomEvent('payload-toast', {
            detail: {
              type: 'success',
              message: result.message || 'Camp erfolgreich geändert!',
            },
          }),
        )

        // Refresh the server component data
        router.refresh()
      } else {
        window.dispatchEvent(
          new CustomEvent('payload-toast', {
            detail: {
              type: 'error',
              message: result.error || 'Fehler beim Ändern des Camps',
            },
          }),
        )
      }
    } catch (error) {
      console.error('Error changing camp:', error)
      window.dispatchEvent(
        new CustomEvent('payload-toast', {
          detail: {
            type: 'error',
            message: 'Fehler beim Ändern des Camps',
          },
        }),
      )
    }
  }

  const handleMoveToCurrentCampClick = (anmeldung: PopulatedAnmeldung) => {
    setAnmeldungToMove(anmeldung)
    setIsMoveConfirmOpen(true)
  }

  const handleMoveToCurrentCamp = async (anmeldungId: string) => {
    if (!currentCampId) return

    setIsMoving(true)
    try {
      // First, use the existing change-camp endpoint to move the camp
      const changeCampResponse = await fetch('/api/admin/camp-anmeldung/change-camp', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          anmeldungId,
          newCampId: currentCampId,
          notes: 'Von alternativem Camp verschoben',
        }),
      })

      const changeCampResult = await changeCampResponse.json()

      if (!changeCampResult.success) {
        window.dispatchEvent(
          new CustomEvent('payload-toast', {
            detail: {
              type: 'error',
              message: changeCampResult.error || 'Fehler beim Verschieben der Anmeldung',
            },
          }),
        )
        return
      }

      // Then, clear the alternativeCamp field using the existing PATCH endpoint
      const clearAlternativeResponse = await fetch(`/api/admin/camp-anmeldung/${anmeldungId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: changeCampResult.anmeldung?.status || 'submitted', // Keep current status
          alternativeCampId: null, // Clear alternative camp
        }),
      })

      const clearAlternativeResult = await clearAlternativeResponse.json()

      if (clearAlternativeResponse.ok) {
        window.dispatchEvent(
          new CustomEvent('payload-toast', {
            detail: {
              type: 'success',
              message: 'Anmeldung erfolgreich zu diesem Camp verschoben!',
            },
          }),
        )

        // Refresh the server component data
        router.refresh()
        // Close the confirmation dialog
        setIsMoveConfirmOpen(false)
        setAnmeldungToMove(null)
        // Switch to main tab since the registration is now in main registrations
        if (onSwitchToMainTab) {
          onSwitchToMainTab()
        }
      } else {
        window.dispatchEvent(
          new CustomEvent('payload-toast', {
            detail: {
              type: 'error',
              message: clearAlternativeResult.error || 'Fehler beim Verschieben der Anmeldung',
            },
          }),
        )
      }
    } catch (error) {
      console.error('Error moving to current camp:', error)
      window.dispatchEvent(
        new CustomEvent('payload-toast', {
          detail: {
            type: 'error',
            message: 'Fehler beim Verschieben der Anmeldung',
          },
        }),
      )
    } finally {
      setIsMoving(false)
    }
  }

  const handleConfirmMove = () => {
    if (anmeldungToMove) {
      handleMoveToCurrentCamp(anmeldungToMove.id)
    }
  }

  const handleBulkStatusUpdate = async (anmeldungIds: string[], newStatus: string) => {
    try {
      const response = await fetch(`/api/admin/camp-anmeldung/bulk-update`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          anmeldungIds,
          status: newStatus,
        }),
      })

      const result = await response.json()

      if (result.success) {
        // Use Payload's toast system
        window.dispatchEvent(
          new CustomEvent('payload-toast', {
            detail: {
              type: 'success',
              message: `${anmeldungIds.length} Anmeldungen erfolgreich aktualisiert!`,
            },
          }),
        )

        // Refresh the server component data
        router.refresh()
      } else {
        window.dispatchEvent(
          new CustomEvent('payload-toast', {
            detail: {
              type: 'error',
              message: result.error || 'Fehler beim Aktualisieren der Status',
            },
          }),
        )
      }
    } catch (error) {
      console.error('Error updating bulk status:', error)
      window.dispatchEvent(
        new CustomEvent('payload-toast', {
          detail: {
            type: 'error',
            message: 'Fehler beim Aktualisieren der Status',
          },
        }),
      )
    }
  }

  const handleEditClick = (anmeldung: PopulatedAnmeldung) => {
    console.log('Edit button clicked for:', anmeldung.id)
    setSelectedAnmeldung(anmeldung)
    setIsDialogOpen(true)
  }

  const handleBulkStatusUpdateClick = () => {
    setIsBulkDialogOpen(true)
  }

  const handleSendEmail = async (anmeldung: PopulatedAnmeldung) => {
    if (!anmeldung.isFreeRegistration) return

    setEmailAnmeldung(anmeldung)
    setIsEmailConfirmOpen(true)
  }

  const confirmSendEmail = async () => {
    if (!emailAnmeldung) return

    try {
      const result = await sendFreeCampConfirmationEmail(emailAnmeldung.id)

      if (result.success) {
        window.dispatchEvent(
          new CustomEvent('payload-toast', {
            detail: {
              type: 'success',
              message: 'Bestätigungs-E-Mail erfolgreich versendet!',
            },
          }),
        )
      } else {
        window.dispatchEvent(
          new CustomEvent('payload-toast', {
            detail: {
              type: 'error',
              message: result.error || 'Fehler beim Versenden der E-Mail',
            },
          }),
        )
      }
    } catch (error) {
      console.error('Error sending email:', error)
      window.dispatchEvent(
        new CustomEvent('payload-toast', {
          detail: {
            type: 'error',
            message: 'Fehler beim Versenden der E-Mail',
          },
        }),
      )
    } finally {
      setIsEmailConfirmOpen(false)
      setEmailAnmeldung(null)
    }
  }

  const isAllSelected = selectedAnmeldungen.size === anmeldungen.length && anmeldungen.length > 0
  const isIndeterminate =
    selectedAnmeldungen.size > 0 && selectedAnmeldungen.size < anmeldungen.length

  return (
    <>
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <table className="w-full table-fixed border-collapse bg-white text-sm">
          <colgroup>
            <col className="w-8" />
            {/* Checkbox */}
            <col className="w-1/4" />
            {/* Teilnehmer */}
            <col className="w-16" />
            {/* Status */}
            <col className="w-18" />
            {/* Zahlungsstatus */}
            <col className="w-16" />
            {/* Leihausrüstung */}
            <col className="w-10" />
            {/* Ticket */}
            <col className="w-10" />
            {/* Anzahl Camps */}
            <col className="w-1/5" />
            {/* Letztes Camp */}
            <col className="w-14" />
            {/* Angemeldet am */}
            <col className="w-12" />
            {/* Aktionen */}
          </colgroup>
          <thead className="border-b border-gray-200 bg-gray-50">
            <tr>
              <th className="px-2 py-3 text-center">
                <EnhancedCheckbox isSelected={isAllSelected} onClick={onSelectAll} size="md" />
              </th>
              <SortableHeader
                field="teilnehmer"
                currentSort={sortField}
                currentDirection={sortDirection}
                onSort={handleSort}
                textAlign="left"
              >
                Teilnehmer
              </SortableHeader>
              <SortableHeader
                field="status"
                currentSort={sortField}
                currentDirection={sortDirection}
                onSort={handleSort}
                textAlign="center"
              >
                Status
              </SortableHeader>
              <SortableHeader
                field="paymentStatus"
                currentSort={sortField}
                currentDirection={sortDirection}
                onSort={handleSort}
                textAlign="center"
              >
                Zahlungsstatus
              </SortableHeader>
              <SortableHeader
                field="needsEquipment"
                currentSort={sortField}
                currentDirection={sortDirection}
                onSort={handleSort}
                textAlign="center"
              >
                Leihausrüstung
              </SortableHeader>
              <SortableHeader
                field="needsLiftTicket"
                currentSort={sortField}
                currentDirection={sortDirection}
                onSort={handleSort}
                textAlign="center"
              >
                Ticket
              </SortableHeader>
              {showMultiPitchColumn && (
                <SortableHeader
                  field="interestMultiPitch"
                  currentSort={sortField}
                  currentDirection={sortDirection}
                  onSort={handleSort}
                  textAlign="center"
                >
                  Mehrseillängen
                </SortableHeader>
              )}
              <SortableHeader
                field="campCount"
                currentSort={sortField}
                currentDirection={sortDirection}
                onSort={handleSort}
                textAlign="center"
              >
                Anz. Camps
              </SortableHeader>
              <SortableHeader
                field="lastCampDate"
                currentSort={sortField}
                currentDirection={sortDirection}
                onSort={handleSort}
                textAlign="left"
              >
                Letztes Camp
              </SortableHeader>
              <SortableHeader
                field="createdAt"
                currentSort={sortField}
                currentDirection={sortDirection}
                onSort={handleSort}
                textAlign="left"
              >
                Angemeldet am
              </SortableHeader>
              <th className="w-12 px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-700">
                Aktionen
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedParticipants.map((participant) => {
              const anmeldung = participant.anmeldungen[0]
              if (!anmeldung) return null

              const isSelected = selectedAnmeldungen.has(anmeldung.id)

              return (
                <tr
                  key={participant.teilnehmer.id}
                  className={`
                    border-b border-gray-100 transition-all duration-200
                    ${isSelected ? 'bg-custom-blue border-blue-200 shadow-sm' : 'hover:bg-gray-50'}
                  `}
                >
                  <td className="p-2 text-center">
                    {!anmeldung.isFreeRegistration && (
                      <EnhancedCheckbox
                        isSelected={isSelected}
                        onClick={() => onSelectAnmeldung(anmeldung.id)}
                        size="md"
                      />
                    )}
                  </td>
                  <td className="border-r border-gray-100 p-4 align-top">
                    <div className="flex items-center gap-3">
                      <ParticipantAvatar
                        teilnehmer={participant.teilnehmer}
                        isFreeRegistration={anmeldung.isFreeRegistration}
                        name={anmeldung.name}
                      />
                      <ParticipantInfo
                        teilnehmer={participant.teilnehmer}
                        isFreeRegistration={anmeldung.isFreeRegistration}
                        name={anmeldung.name}
                        duplicateHints={anmeldung.duplicateHints}
                      />
                    </div>
                  </td>
                  <td className="border-r border-gray-100 p-4 text-center align-top">
                    <div className="flex flex-col items-center gap-0.5">
                      <StatusBadge status={participant.latestStatus} />
                      {participant.latestStatus === 'confirmed' &&
                        (() => {
                          const confirmedWithVerifiedAt = participant.anmeldungen.find(
                            (a) => a.status === 'confirmed' && (a as any).verifiedAt,
                          ) as any
                          const formatted = formatVerifiedDate(confirmedWithVerifiedAt?.verifiedAt)
                          return formatted ? (
                            <span className="text-xs text-gray-500">({formatted})</span>
                          ) : null
                        })()}
                    </div>
                  </td>
                  <td className="border-r border-gray-100 p-4 text-center align-top">
                    <PaymentStatusBadge anmeldung={anmeldung} />
                  </td>
                  <td className="border-r border-gray-100 p-4 text-center align-top">
                    {anmeldung.isFreeRegistration ? (
                      <span className="text-xs text-gray-400">-</span>
                    ) : (
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold uppercase tracking-wide border ${
                          anmeldung.needsEquipment
                            ? 'bg-green-100 text-green-800 border-green-300'
                            : 'bg-gray-100 text-gray-600 border-gray-300'
                        }`}
                      >
                        {anmeldung.needsEquipment ? 'Ja' : 'Nein'}
                      </span>
                    )}
                  </td>
                  <td className="border-r border-gray-100 p-4 text-center align-top">
                    {anmeldung.isFreeRegistration ? (
                      <span className="text-xs text-gray-400">-</span>
                    ) : (
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold uppercase tracking-wide border ${
                          anmeldung.needsLiftTicket
                            ? 'bg-green-100 text-green-800 border-green-300'
                            : 'bg-gray-100 text-gray-600 border-gray-300'
                        }`}
                      >
                        {anmeldung.needsLiftTicket ? 'Ja' : 'Nein'}
                      </span>
                    )}
                  </td>
                  {showMultiPitchColumn && (
                    <td className="border-r border-gray-100 p-4 text-center align-top">
                      {anmeldung.isFreeRegistration ? (
                        <span className="text-xs text-gray-400">-</span>
                      ) : (
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold uppercase tracking-wide border ${
                            anmeldung.interestMultiPitch
                              ? 'bg-green-100 text-green-800 border-green-300'
                              : 'bg-gray-100 text-gray-600 border-gray-300'
                          }`}
                        >
                          {anmeldung.interestMultiPitch ? 'Ja' : 'Nein'}
                        </span>
                      )}
                    </td>
                  )}
                  <td className="border-r border-gray-100 p-4 text-center align-top">
                    <div className="text-base font-semibold text-gray-700">
                      {participant.campCount}
                    </div>
                    {(anmeldung.openFutureRegistrations?.length ?? 0) > 0 && (
                      <HoverCard openDelay={100} closeDelay={150}>
                        <HoverCardTrigger asChild>
                          <span
                            tabIndex={0}
                            className="mt-1 inline-flex cursor-help items-center whitespace-nowrap rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800"
                          >
                            {anmeldung.openFutureRegistrations!.length} offen
                          </span>
                        </HoverCardTrigger>
                        <HoverCardContent className="text-left">
                          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Offene Anmeldungen für künftige Camps
                          </p>
                          <ul className="space-y-1.5">
                            {anmeldung.openFutureRegistrations!.map((open) => {
                              const params = new URLSearchParams({
                                camp: open.campId,
                                view: 'details',
                              })
                              if (open.email) params.set('emailSearch', open.email)
                              return (
                                <li key={`${open.campId}-${open.email}`}>
                                  <a
                                    href={`?${params.toString()}`}
                                    className="text-amber-800 underline hover:text-amber-950"
                                  >
                                    {open.date
                                      ? new Date(open.date).toLocaleDateString('de-DE')
                                      : '-'}
                                    {open.title ? ` ${open.title}` : ''}
                                    {open.otherEmail ? ' *' : ''}
                                  </a>
                                  {open.otherEmail && (
                                    <span className="block text-xs text-gray-500">
                                      Mit anderer E-Mail ({open.email}), gleicher Name und gleiches
                                      Geburtsdatum
                                    </span>
                                  )}
                                </li>
                              )
                            })}
                          </ul>
                        </HoverCardContent>
                      </HoverCard>
                    )}
                  </td>
                  <td className="border-r border-gray-100 p-4 align-top">
                    <div className="space-y-1">
                      {participant.pastCamps && participant.pastCamps.length > 0 ? (
                        participant.pastCamps.map((camp, index) => (
                          <div
                            key={camp.id || index}
                            className={`text-sm ${
                              camp.isFuture ? 'text-red-600 font-semibold' : 'text-gray-700'
                            }`}
                          >
                            {camp.date ? new Date(camp.date).toLocaleDateString('de-DE') : '-'}
                            {camp.title ? ` (${camp.title})` : ''}
                          </div>
                        ))
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </div>
                  </td>
                  <td className="border-r border-gray-100 p-4 align-top">
                    <div className="font-medium text-gray-700">
                      <span className="flex text-xs text-gray-500">
                        {new Date(participant.anmeldungen[0]?.createdAt || '').toLocaleDateString(
                          'de-DE',
                        )}
                      </span>
                      <span className="text-xs text-gray-500">
                        {new Date(participant.anmeldungen[0]?.createdAt || '').toLocaleTimeString(
                          'de-DE',
                          {
                            hour: '2-digit',
                            minute: '2-digit',
                          },
                        )}
                      </span>
                    </div>
                  </td>
                  <td className="p-4 align-top">
                    <div className="flex flex-col items-center gap-2">
                      {anmeldung.isFreeRegistration ? (
                        anmeldung.email ? (
                          <button
                            onClick={() => handleSendEmail(anmeldung)}
                            className="min-w-10 flex h-10 cursor-pointer items-center justify-center rounded-md border border-green-500 bg-green-50 p-2 text-green-700 transition-all duration-150 hover:bg-green-100"
                            title="Bestätigungs-E-Mail senden"
                          >
                            <Mail className="h-4 w-4" />
                          </button>
                        ) : (
                          <span className="text-xs italic text-gray-400">Keine E-Mail</span>
                        )
                      ) : isAlternativeView && currentCampId ? (
                        <button
                          onClick={() => handleMoveToCurrentCampClick(anmeldung)}
                          className="min-w-10 flex h-10 cursor-pointer items-center justify-center rounded-md border border-orange-500 bg-orange-50 p-2 text-orange-700 transition-all duration-150 hover:bg-orange-100"
                          title="Zu diesem Camp verschieben"
                        >
                          <ArrowRight className="h-4 w-4" />
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => handleEditClick(anmeldung)}
                            className="min-w-10 flex h-10 cursor-pointer items-center justify-center rounded-md border border-blue-500 bg-custom-blue p-2 text-blue-700 transition-all duration-150 hover:bg-blue-100"
                            title="Status bearbeiten"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <StatusManagementDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        anmeldung={selectedAnmeldung}
        onStatusUpdate={handleStatusUpdate}
        onCampChange={handleCampChange}
      />

      <BulkStatusManagementDialog
        isOpen={isBulkDialogOpen}
        onClose={() => setIsBulkDialogOpen(false)}
        selectedAnmeldungen={Array.from(selectedAnmeldungen)}
        anmeldungen={anmeldungen}
        onBulkStatusUpdate={handleBulkStatusUpdate}
      />

      {/* Move to Camp Confirmation Dialog */}
      <Dialog
        open={isMoveConfirmOpen}
        onOpenChange={(open) => {
          if (!isMoving) {
            setIsMoveConfirmOpen(open)
            if (!open) {
              setAnmeldungToMove(null)
            }
          }
        }}
      >
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Anmeldung verschieben</DialogTitle>
            <DialogDescription className="text-gray-700">
              Möchten Sie diese Anmeldung wirklich zu diesem Camp verschieben?
            </DialogDescription>
          </DialogHeader>
          {anmeldungToMove && (
            <div className="space-y-3 text-sm text-gray-700">
              <div className="rounded-md bg-gray-50 p-3">
                <div className="space-y-2">
                  <div>
                    <span className="font-medium">Teilnehmer:</span>{' '}
                    {anmeldungToMove.isFreeRegistration
                      ? anmeldungToMove.name
                      : `${(anmeldungToMove.teilnehmer as Teilnehmer)?.firstName || ''} ${(anmeldungToMove.teilnehmer as Teilnehmer)?.lastName || ''}`.trim()}
                  </div>
                  <div>
                    <span className="font-medium">Aktuelles Camp:</span>{' '}
                    {(anmeldungToMove.camp as Camp)?.title || 'Unbekannt'}
                  </div>
                  <div>
                    <span className="font-medium">Status:</span>{' '}
                    {anmeldungToMove.status || 'Unbekannt'}
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-600">
                Die Anmeldung wird von ihrem aktuellen Camp zu diesem Camp verschoben. Das
                alternative Camp wird entfernt.
              </p>
            </div>
          )}
          {isMoving && (
            <div className="flex items-center justify-center py-4">
              <div className="flex flex-col items-center space-y-2">
                <div className="h-6 w-6 animate-spin rounded-full border-4 border-gray-300 border-t-orange-600"></div>
                <p className="text-sm text-gray-600">Anmeldung wird verschoben...</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <button
              onClick={() => {
                setIsMoveConfirmOpen(false)
                setAnmeldungToMove(null)
              }}
              disabled={isMoving}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Abbrechen
            </button>
            <button
              onClick={handleConfirmMove}
              disabled={isMoving}
              className="rounded-md border border-transparent bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isMoving ? 'Wird verschoben...' : 'Ja, verschieben'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Confirmation Dialog */}
      {isEmailConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={() => setIsEmailConfirmOpen(false)}
          />

          {/* Modal */}
          <div className="relative mx-4 w-full max-w-md rounded-lg bg-white shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-200 p-6">
              <h2 className="flex items-center gap-2 text-lg font-semibold">
                <Mail className="h-5 w-5 text-green-600" />
                E-Mail bestätigen
              </h2>
              <button
                onClick={() => setIsEmailConfirmOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Content */}
            <div className="space-y-4 p-6">
              <p className="text-sm text-gray-600">
                Möchten Sie wirklich eine Bestätigungs-E-Mail senden?
              </p>

              {emailAnmeldung && (
                <div className="rounded-lg bg-gray-50 p-4">
                  <div className="space-y-2">
                    <div>
                      <span className="font-medium">An:</span> {emailAnmeldung.name}
                    </div>
                    <div>
                      <span className="font-medium">E-Mail:</span> {emailAnmeldung.email}
                    </div>
                    <div>
                      <span className="font-medium">Camp:</span>{' '}
                      {(emailAnmeldung.camp as Camp)?.title}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 border-t border-gray-200 p-6">
              <button
                onClick={() => setIsEmailConfirmOpen(false)}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Abbrechen
              </button>
              <button
                onClick={confirmSendEmail}
                className="rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              >
                E-Mail senden
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
export default AnmeldungenTable
